import { uiText } from '../../i18n/uiText';
import React, { useEffect, useState, useMemo, useRef } from "react";
import Dialog from '../common/Dialog';
import { Link } from "react-router-dom";
import { X, Save, ClipboardList, CheckCircle2, Info, ChevronRight } from "lucide-react";
import {
  auditsApi,
  templatesApi,
  usersApi,
  companiesApi,
  auditProgramsApi,
  contractsApi,
  signatoriesApi,
  getApiErrorMessage,
} from "../../api";
import { useAuth } from "../../store/AuthContext";
import { formatDate, formatStatus } from "../../utils/formatters";
import TemplatePickerModal, { isTemplateCompatible } from "./TemplatePickerModal";
import useDraftBuffer from '../../hooks/useDraftBuffer';
import FormDraftNotice from '../common/FormDraftNotice';

const STATUS_OPTIONS = [
  { value: "not_started", label: uiText("Не начат") },
  { value: "in_progress", label: uiText("В работе") },
  { value: "ready", label: uiText("Готов к закрытию") },
  { value: "completed", label: uiText("Завершён") },
];

const CONTROL_MODES = [
  { value: "none", label: uiText("Контроль не требуется") },
  { value: "aas", label: uiText("Контроль ведёт ААС") },
  { value: "customer", label: uiText("Контроль ведёт заказчик / объект") },
];

function toDateTimeInputValue(value) {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return `${value}T10:00`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 16);
}

function currentDateTimeInputValue() {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  return new Date(now.getTime() - offset * 60000).toISOString().slice(0, 16);
}

function isPastDateTime(value) {
  if (!value) return false;
  const parsed = new Date(value);
  return !Number.isNaN(parsed.getTime()) && parsed.getTime() < Date.now() - 60_000;
}

export default function AuditUpsertModal({
  mode = "create",
  audit = null,
  initialValues = null,
  preparedChecklist = false,
  onClose,
  onSaved,
}) {
  const { user } = useAuth();
  const isEdit = mode === "edit";
  const isAdmin = user?.role === "admin";
  const isAuditor = user?.role === "auditor";
  const meta = audit?.meta || {};
  const modern = !isEdit || meta.workflow_version === 'compliance-v2';
  // R11 п.1: pre-fill при создании аудита из карточки компании (?audited_company_id=X).
  // initialValues — это {audited_company_id, title}. Применяется только в режиме create.
  const iv = (!isEdit && initialValues) ? initialValues : {};

  const [initialForm] = useState({
    title: audit?.title || iv.title || "",
    audit_mode: meta.audit_mode || 'audit',
    description: audit?.description || "",
    template_id: audit?.template?.id ? String(audit.template.id) : (iv.template_id ? String(iv.template_id) : ""),
    auditor_id: audit?.auditor?.id
      ? String(audit.auditor.id)
      : isAuditor
        ? String(user?.id || "")
        : "",
    customer_id: audit?.customer?.id ? String(audit.customer.id) : "",
    // UI-only flag. Persistence remains customer_id=null + control_mode=aas,
    // therefore the change needs no schema migration.
    without_representative: Boolean(
      !audit?.customer?.id && ["aas", "auditor", "none"].includes(meta?.control_mode),
    ),
    scheduled_date: toDateTimeInputValue(audit?.scheduled_date) || toDateTimeInputValue(iv.scheduled_date) || "",
    status: audit?.status || "not_started",
    audited_company_id: meta?.audited_company?.id
      ? String(meta.audited_company.id)
      : (iv.audited_company_id ? String(iv.audited_company_id) : ""),
    customer_company_id: meta?.customer_company?.id
      ? String(meta.customer_company.id)
      : "",
    external_customer_name: meta?.external_customer_name || "",
    site_address: meta?.site_address || "",
    // Тип аудита: код из таксономии. Если в meta уже лежит код — берём,
    // если осталась старая свободная строка — подставится дефолт после загрузки таксономии.
    audit_type: meta?.audit_type || "compliance",
    // Тип объекта проверки лежит в meta.category для обратной совместимости.
    subject_kind: meta?.category || "supplier",
    regulation_profile: meta?.regulation_profile || "procedure",
    // Сохраняем поле category отдельно — на бэке оно выбирается через subject_kind,
    // но если кто-то писал в category вручную, не теряем значение.
    category: meta?.category || "",
    control_mode: meta?.control_mode || "customer",
    audit_basis: meta?.audit_basis || "",
    signature_name: meta?.signature_name || "",
    program_id: meta?.program_id ? String(meta.program_id) : "",
    // Договор-основание и подписант приказа.
    contract_id: meta?.contract_id ? String(meta.contract_id) : "",
    signatory_id: meta?.signatory_id ? String(meta.signatory_id) : "",
  });

  const buffer = useDraftBuffer(user?.id, isEdit ? audit?.id : preparedChecklist ? `prepared-${iv.template_id}-${iv.audited_company_id}` : `new-${iv.audited_company_id || 'registry'}`, 'audit-properties');
  const form = buffer.values.form || initialForm;
  const setForm = update => { if (!saving && !buffer.recovery) buffer.edit('form', typeof update === 'function' ? update(buffer.values.form || initialForm) : update); };

  const [templates, setTemplates] = useState([]);
  const [auditors, setAuditors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [signatories, setSignatories] = useState([]);
  const [companySummary, setCompanySummary] = useState(null);
  const [taxonomy, setTaxonomy] = useState({ audit_kinds: [], subject_kinds: [], regulations: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [activeStep, setActiveStep] = useState("basic");
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const templateTriggerRef = useRef(null);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const loaders = [
          ["templates", templatesApi.list("published", { summary: true })],
          ["customers", usersApi.customers()],
          ["companies", companiesApi.list()],
          ["programs", auditProgramsApi.list()],
          ["taxonomy", auditsApi.taxonomy()],
          ["contracts", contractsApi.list({ status: 'active' })],
          ["signatories", signatoriesApi.list({ only_active: true })],
        ];

        if (isAdmin || !isEdit) {
          loaders.push(["auditors", usersApi.auditors()]);
        }

        const results = await Promise.allSettled(loaders.map(([, request]) => request));

        if (!alive) return;

        const responses = {};
        const failures = [];
        results.forEach((result, index) => {
          const [key] = loaders[index];
          if (result.status === "fulfilled") {
            responses[key] = result.value;
          } else {
            failures.push({ key, error: result.reason });
          }
        });

        const requiredKeys = ["templates", "customers"];
        if (isAdmin || !isEdit) requiredKeys.push("auditors");
        const fatalFailure = failures.find((item) => requiredKeys.includes(item.key));
        if (fatalFailure) throw fatalFailure.error;

        setTemplates(Array.isArray(responses.templates?.data) ? responses.templates.data : []);
        setCustomers(Array.isArray(responses.customers?.data) ? responses.customers.data : []);
        setCompanies(Array.isArray(responses.companies?.data) ? responses.companies.data : []);
        setPrograms(Array.isArray(responses.programs?.data) ? responses.programs.data : []);
        setTaxonomy(responses.taxonomy?.data || { audit_kinds: [], subject_kinds: [], regulations: [] });
        setContracts(Array.isArray(responses.contracts?.data) ? responses.contracts.data : []);
        setSignatories(Array.isArray(responses.signatories?.data) ? responses.signatories.data : []);
        setAuditors(Array.isArray(responses.auditors?.data) ? responses.auditors.data : []);
      } catch (e) {
        if (!alive) return;
        setError(getApiErrorMessage(e));
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();

    return () => {
      alive = false;
    };
  }, [isAdmin, isEdit]);

  const auditorLocked = isAuditor && !isAdmin;
  const customerLocked = isEdit && !(isAdmin || isAuditor);
  const templateLocked = isEdit || preparedChecklist;

  const selectedTemplate = useMemo(() => (
    templates.find((item) => String(item.id) === String(form.template_id))
    || (audit?.template && String(audit.template.id) === String(form.template_id) ? audit.template : null)
  ), [audit?.template, form.template_id, templates]);

  const selectedTemplateCompatible = selectedTemplate
    ? (modern || isTemplateCompatible(selectedTemplate, form.audit_type))
    : false;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleRepresentativeModeChange = (enabled) => {
    setForm((prev) => ({
      ...prev,
      without_representative: enabled,
      customer_id: enabled ? "" : prev.customer_id,
      control_mode: enabled ? "aas" : prev.control_mode,
    }));
  };

  const handleCustomerChange = (value) => {
    setForm((prev) => ({
      ...prev,
      customer_id: value,
      without_representative: value ? false : prev.without_representative,
    }));
  };

  const closeTemplatePicker = () => {
    setTemplatePickerOpen(false);
    window.requestAnimationFrame(() => templateTriggerRef.current?.focus());
  };

  const formSteps = [
    {
      key: "basic",
      label: uiText("Основные данные"),
      complete: Boolean(form.title.trim()),
    },
    {
      key: "participants",
      label: uiText("Чек-лист и аудитор"),
      complete: Boolean(form.auditor_id && form.template_id && selectedTemplateCompatible),
    },
    {
      key: "classification",
      label: uiText("Компании и тип"),
      complete: Boolean(form.audited_company_id && form.audit_type && form.subject_kind),
    },
    {
      key: "schedule",
      label: uiText("Сроки и документы"),
      complete: Boolean(form.scheduled_date),
    },
    {
      key: "review",
      label: uiText("Проверка"),
      complete: Boolean(form.title && form.auditor_id && form.template_id && form.audited_company_id && form.audit_type && form.subject_kind),
    },
  ];

  const goToStep = (key) => {
    setActiveStep(key);
    document.getElementById(`audit-form-step-${key}`)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  // При смене типа аудита автоматически выставляем дефолтный тип объекта
  // и регламент-источник (берём из таксономии). Пользователь может потом
  // переопределить вручную, если нужно.
  const handleAuditKindChange = (code) => {
    const meta = (taxonomy.audit_kinds || []).find((k) => k.code === code);
    setForm((prev) => ({
      ...prev,
      audit_type: code,
      subject_kind: meta?.default_subject_kind || prev.subject_kind,
      regulation_profile: meta?.regulation || prev.regulation_profile,
    }));
  };

  const selectedAuditKind = (taxonomy.audit_kinds || []).find((k) => k.code === form.audit_type);
  const selectedRegulation = (taxonomy.regulations || []).find((r) => r.code === form.regulation_profile);

  // Договоры не скрываем: при планировании часто есть внешний заказ или
  // будущий подрядчик без прямого договора с проверяемым объектом. Сначала
  // показываем релевантные, затем остальные действующие договоры.
  const filteredContracts = useMemo(() => {
    if (!contracts.length) return [];
    const auditedId = form.audited_company_id ? Number(form.audited_company_id) : null;
    const customerId = form.customer_company_id ? Number(form.customer_company_id) : null;
    if (!auditedId && !customerId) return contracts;
    const scored = contracts.map((c) => {
      const relevant =
        (auditedId && (c.contractor_company_id === auditedId || c.customer_company_id === auditedId))
        || (customerId && (c.customer_company_id === customerId || c.contractor_company_id === customerId));
      return { contract: c, score: relevant ? 0 : 1 };
    });
    return scored.sort((a, b) => a.score - b.score).map((item) => item.contract);
  }, [contracts, form.audited_company_id, form.customer_company_id]);

  const hasRelevantContracts = useMemo(() => {
    const auditedId = form.audited_company_id ? Number(form.audited_company_id) : null;
    const customerId = form.customer_company_id ? Number(form.customer_company_id) : null;
    if (!auditedId && !customerId) return true;
    return contracts.some((c) => {
      if (auditedId && (c.contractor_company_id === auditedId || c.customer_company_id === auditedId)) return true;
      if (customerId && (c.customer_company_id === customerId || c.contractor_company_id === customerId)) return true;
      return false;
    });
  }, [contracts, form.audited_company_id, form.customer_company_id]);

  // Подписанты фильтруются по компании-Заказчику (это орг-инициатор приказа).
  const filteredSignatories = useMemo(() => {
    if (!signatories.length) return [];
    const customerId = form.customer_company_id ? Number(form.customer_company_id) : null;
    if (!customerId) return signatories;
    return signatories.filter((s) => s.company_id === customerId && s.can_sign_orders);
  }, [signatories, form.customer_company_id]);

  const filteredCustomers = useMemo(() => {
    const auditedCompanyId = form.audited_company_id ? Number(form.audited_company_id) : null;
    if (!auditedCompanyId) return [];
    return customers.filter((item) => (
      Number(item.company_profile?.company_id) === auditedCompanyId
      || String(item.id) === String(form.customer_id)
    ));
  }, [customers, form.audited_company_id, form.customer_id]);

  const handleAuditedCompanyChange = (id) => {
    const company = companies.find((item) => String(item.id) === String(id));
    const cats = company?.category_types || [];
    const inferred =
      cats.includes('partner') ? 'partner'
      : cats.includes('contractor') ? 'contractor'
      : cats.includes('supplier') ? 'supplier'
      : cats.includes('ecosystem_entity') ? 'internal_subdivision'
      : null;
    const defaultSiteAddress = company?.site_address || company?.legal_address || '';

    setForm((prev) => ({
      ...prev,
      audited_company_id: id,
      customer_id: '',
      without_representative: Boolean(company && Number(company.member_count || 0) === 0),
      site_address: defaultSiteAddress,
      ...(company && Number(company.member_count || 0) === 0 ? { control_mode: 'aas' } : {}),
      ...(inferred ? {
        subject_kind: inferred,
        regulation_profile: inferred === 'partner' ? 'extended' : 'procedure',
      } : {}),
    }));
  };

  // Загрузка истории аудитов выбранной компании
  useEffect(() => {
    const cid = form.audited_company_id;
    if (!cid) { setCompanySummary(null); return; }
    let alive = true;
    companiesApi.auditsSummary(cid)
      .then((res) => { if (alive) setCompanySummary(res.data); })
      .catch(() => { if (alive) setCompanySummary(null); });
    return () => { alive = false; };
  }, [form.audited_company_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || loading || buffer.recovery) return;
    const snapshot = buffer.snapshot();
    setSaving(true);
    setError("");

    try {
      if (!form.title.trim()) {
        throw new Error(uiText("Укажи название аудита"));
      }
      if (!isEdit && !form.template_id) {
        throw new Error(uiText("Выбери шаблон"));
      }
      if (!isEdit && selectedTemplate && !selectedTemplateCompatible) {
        throw new Error(uiText("Выбранный шаблон не подходит для указанного типа аудита"));
      }
      if (!form.auditor_id) {
        throw new Error(uiText("Выбери аудитора"));
      }
      if (!form.audited_company_id) {
        throw new Error(uiText("Выберите проверяемую компанию"));
      }
    if (!isEdit && !form.customer_id && !form.without_representative) {
        throw new Error(uiText("Выберите представителя или включите режим «Без представителя»"));
      }
      if (modern && form.audit_mode === 'self_audit' && (!form.customer_id || form.without_representative)) {
        throw new Error(uiText("Для самоаудита выберите представителя: он будет заполнять чек-лист."));
      }
      if (!form.audit_type || !form.subject_kind) {
        throw new Error(uiText("Укажи тип аудита и тип объекта проверки"));
      }
      if (!isEdit && isPastDateTime(form.scheduled_date)) {
        throw new Error(uiText("Плановая дата и время не могут быть в прошлом"));
      }

      const payload = {
        title: form.title,
        description: form.description,
        scheduled_date: form.scheduled_date || null,
        audited_company_id: form.audited_company_id
          ? Number(form.audited_company_id)
          : null,
        customer_company_id: form.customer_company_id
          ? Number(form.customer_company_id)
          : null,
        external_customer_name: modern ? null : (form.external_customer_name || null),
        ...(!isEdit ? { audit_mode: form.audit_mode || 'audit' } : {}),
        site_address: form.site_address || null,
        // Главная связка: тип аудита, тип объекта проверки и регламент-источник.
        // Бэк валидирует значения по таксономии (см. audit_taxonomy.py).
        audit_type: modern ? 'compliance' : form.audit_type,
        subject_kind: modern ? undefined : form.subject_kind,
        regulation_profile: modern ? 'procedure' : (form.regulation_profile || null),
        control_mode: form.control_mode || "customer",
        audit_basis: form.audit_basis || null,
        signature_name: form.signature_name || null,
        program_id: isEdit && form.program_id ? Number(form.program_id) : null,
        contract_id: form.contract_id ? Number(form.contract_id) : null,
        signatory_id: form.signatory_id ? Number(form.signatory_id) : null,
      };

      if (isEdit) {
        payload.status = form.status;

        if (isAdmin) {
          payload.auditor_id = Number(form.auditor_id);
        }
        if (isAdmin || isAuditor) {
          payload.customer_id = form.customer_id
            ? Number(form.customer_id)
            : null;
        }

        const res = await auditsApi.update(audit.id, payload);
        buffer.acknowledge(snapshot, { committed: true });
        if (buffer.active) onSaved?.(res?.data);
      } else {
        const res = await auditsApi.create({
          ...payload,
          template_id: Number(form.template_id),
          auditor_id: Number(form.auditor_id),
          customer_id: form.customer_id ? Number(form.customer_id) : null,
        });
        buffer.acknowledge(snapshot, { committed: true });
        if (buffer.active) onSaved?.(res?.data);
      }
    } catch (e2) {
      setError(getApiErrorMessage(e2) || e2.message || uiText("Не удалось сохранить аудит"));
    } finally {
      setSaving(false);
    }
  };

  const modalNode = (
    <Dialog size="xl" label={isEdit ? uiText("Редактирование аудита") : uiText("Создание аудита")} onClose={onClose} closeDisabled={saving}>
        <div className="ap-dialog__header">
          <div
            className="ap-dialog__title"
            data-dialog-heading tabIndex={-1}
            style={{ display: "flex", alignItems: "center", gap: 10 }}
          >
            {isEdit ? <Save size={18} /> : <ClipboardList size={18} />}
            {isEdit ? uiText("Редактирование аудита") : uiText("Создание аудита")}
          </div>

          <button
            type="button"
            className="ap-dialog__close"
            disabled={saving}
            aria-label={uiText("Закрыть")}
            title={uiText("Закрыть")}
            onClick={onClose}
          >
            <X size={16} />
          </button>
        </div>

        <form className="audit-upsert-form" onSubmit={handleSubmit}>
          <div className="ap-dialog__body audit-upsert-body">
            {error ? <div className="alert alert-error">{error}</div> : null}
            <FormDraftNotice buffer={buffer} label={isEdit ? uiText("Свойства аудита") : uiText("Новый аудит")} disabled={saving || loading} />

            {loading ? (
              <div className="loading-center" style={{ padding: '40px 20px' }}>
                <div className="spinner" />
                <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{uiText("Загрузка справочников…")}</span>
              </div>
            ) : (
              <>
                {!isEdit && templates.length === 0 ? (
                  <div className="alert alert-warning">{uiText("Нет опубликованных шаблонов чек-листов. Сначала опубликуйте хотя бы один шаблон в разделе шаблонов.")}</div>
                ) : null}

                <div className="ap-stepper" aria-label={uiText("Этапы формы аудита")}>
                  {formSteps.map((step, index) => (
                    <button
                      key={step.key}
                      type="button"
                      className={`ap-stepper-button ${activeStep === step.key ? "is-active" : ""} ${step.complete ? "is-complete" : ""}`}
                      aria-current={activeStep === step.key ? "step" : undefined}
                      onClick={() => goToStep(step.key)}
                    >
                      <span className="ap-stepper-number">
                        {step.complete ? <CheckCircle2 size={13} /> : index + 1}
                      </span>
                      {step.label}
                    </button>
                  ))}
                </div>

                <div className="ap-dialog__note">
                  <Info size={16} />
                  <span>{uiText("Заполняйте форму сверху вниз. Навигация по этапам перемещает к нужному блоку и не сбрасывает уже введённые данные.")}</span>
                </div>

                <div className="ap-step-anchor" id="audit-form-step-basic">
                  <div className="ap-step-anchor-title">{uiText("1. Основные данные")}</div>
                  <div className="ap-step-anchor-description">{uiText("Название и краткое описание проверки.")}</div>
                </div>

                <div className="form-group">
                  <label className="form-label required">{uiText("Название аудита")}</label>
                  <input
                    className="ap-control"
                    value={form.title}
                    onChange={(e) => handleChange("title", e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">{uiText("Описание")}</label>
                  <textarea
                    className="ap-control"
                    rows={3}
                    value={form.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                  />
                </div>

                <div className="ap-step-anchor" id="audit-form-step-participants">
                  <div className="ap-step-anchor-title">{uiText("2. Чек-лист и аудитор")}</div>
                  <div className="ap-step-anchor-description">{uiText("Выберите рабочий шаблон и ответственного аудитора.")}</div>
                </div>

                <div className="form-row audit-upsert-grid audit-upsert-participants-grid">
                  <div className="form-group">
                    <label className="form-label required">{uiText("Шаблон")}</label>
                    <button
                      ref={templateTriggerRef}
                      type="button"
                      className={`audit-template-picker-trigger${form.template_id && !selectedTemplateCompatible ? " is-invalid" : ""}`}
                      disabled={templateLocked}
                      aria-haspopup="dialog"
                      aria-expanded={templatePickerOpen}
                      onClick={() => setTemplatePickerOpen(true)}
                    >
                      <span className="audit-template-picker-trigger-icon" aria-hidden="true"><ClipboardList size={17} /></span>
                      <span className="audit-template-picker-trigger-copy">
                        <strong>{selectedTemplate?.name || uiText("Выберите чек-лист аудита")}</strong>
                        <span>
                          {selectedTemplate
                            ? uiText("{v0} · создан {v1}", {v0: (selectedTemplate.version || uiText("Версия не указана")), v1: (formatDate(selectedTemplate.created_at))})
                            : uiText("{v0} опубликованных шаблонов · поиск и предварительный просмотр", {v0: (templates.length)})}
                        </span>
                      </span>
                      <ChevronRight size={17} className="audit-template-picker-trigger-chevron" aria-hidden="true" />
                    </button>
                    <div className={`audit-template-picker-help${form.template_id && !selectedTemplateCompatible ? " is-warning" : ""}`}>
                      <Info size={13} />
                      <span>
                        {form.template_id && !selectedTemplateCompatible
                          ? uiText("Шаблон не подходит для выбранного типа аудита. Выберите другой шаблон.")
                          : templateLocked
                            ? preparedChecklist ? 'Чек-лист подготовлен на предыдущем шаге. Для изменения состава вернитесь в мастер.' : uiText("Шаблон зафиксирован при создании аудита и не изменяется.")
                            : uiText("Откройте каталог, чтобы найти шаблон по названию и проверить его вопросы.")}
                      </span>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label required">{uiText("Аудитор")}</label>
                    <select
                      className="ap-control"
                      value={form.auditor_id}
                      onChange={(e) => handleChange("auditor_id", e.target.value)}
                      disabled={auditorLocked}
                      required
                    >
                      <option value="">{uiText("Выберите аудитора...")}</option>
                      {(auditors.length ? auditors : user ? [user] : []).map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.full_name}
                        </option>
                      ))}
                    </select>
                    <div className="audit-upsert-field-help">{uiText("В списке отображаются активные аудиторы. Если специалиста нет, добавьте его в ")}<Link to="/auditor-pool" target="_blank" rel="noreferrer">{uiText("пуле аудиторов")}</Link>.</div>
                  </div>

                </div>

                <div className="ap-step-anchor" id="audit-form-step-classification">
                  <div className="ap-step-anchor-title">{uiText("3. Компании и тип аудита")}</div>
                  <div className="ap-step-anchor-description">{uiText("Укажите участников, объект проверки и проверьте автоматически подобранный регламент.")}</div>
                </div>

                <div className="form-row audit-upsert-grid audit-upsert-organizations-grid">
                  <div className="form-group">
                    <label className="form-label required">{uiText("Проверяемый объект (компания)")}</label>
                    <select
                      className="ap-control"
                      value={form.audited_company_id}
                      disabled={preparedChecklist && !isEdit}
                      onChange={(e) => handleAuditedCompanyChange(e.target.value)}
                      required
                    >
                      <option value="">{uiText("Не выбрана")}</option>
                      {companies.map((item) => {
                        const cats = item.category_types || [];
                        const tag = cats.includes('partner') ? uiText(" [расширенный регламент]")
                          : cats.includes('contractor') ? uiText(" [подрядчик]")
                          : cats.includes('supplier') ? uiText(" [поставщик]")
                          : cats.includes('ecosystem_entity') ? uiText(" [экосистема]")
                          : '';
                        return (
                          <option key={item.id} value={item.id}>
                            {item.name}{tag}{Number(item.member_count || 0) === 0 ? uiText(" · без представителя") : ''}
                          </option>
                        );
                      })}
                    </select>
                    <div className="ap-field__hint" style={{ marginTop: 4 }}>{uiText("Тип объекта проверки и регламент будут подставлены автоматически по типу компании. Нет нужной организации? ")}<Link to="/companies" target="_blank" rel="noreferrer">{uiText("Создайте её в справочнике")}</Link>.
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{uiText("Заказчик аудита (компания)")}</label>
                    <select
                      className="ap-control"
                      value={form.customer_company_id}
                      onChange={(e) =>
                        handleChange("customer_company_id", e.target.value)
                      }
                    >
                      <option value="">{uiText("Не выбрана")}</option>
                      {companies.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                    <div style={{ fontSize: 11, color: 'var(--premium-text-secondary)', marginTop: 4 }}>{uiText("Кто инициирует аудит. По регламенту — заказчик аудита.")}</div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{uiText("Контактное лицо проверяемой организации")}</label>
                    {!isEdit && form.audit_mode !== 'self_audit' ? (
                      <label className="audit-upsert-no-representative-toggle">
                        <input
                          type="checkbox"
                          className="ap-check"
                          checked={form.without_representative}
                          onChange={(e) => handleRepresentativeModeChange(e.target.checked)}
                        />
                        <span>
                          <strong>{uiText("Проводить аудит без представителя")}</strong>
                          <small>{uiText("Внешний вход, письма и согласования не требуются; документы и подтверждения фиксирует команда аудита.")}</small>
                        </span>
                      </label>
                    ) : null}
                    <select
                      className="ap-control"
                      value={form.customer_id}
                      onChange={(e) => handleCustomerChange(e.target.value)}
                      disabled={customerLocked || !form.audited_company_id || (!isEdit && form.without_representative)}
                    >
                      <option value="">
                        {form.audited_company_id
                          ? (filteredCustomers.length ? uiText("Не выбрано") : uiText("Нет привязанных сотрудников"))
                          : uiText("Сначала выберите проверяемый объект")}
                      </option>
                      {filteredCustomers.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.full_name}{item.is_active === false ? uiText(" · ожидает регистрации") : ""}
                        </option>
                      ))}
                    </select>
                    <div style={{ fontSize: 11, color: 'var(--premium-text-secondary)', marginTop: 4 }}>{uiText("Показываются только сотрудники выбранной компании, включая приглашённых пользователей. Добавить представителя можно в ")}<Link to="/companies" target="_blank" rel="noreferrer">{uiText("карточке компании")}</Link>.
                    </div>
                    {!isEdit && form.without_representative && (
                      <div className="audit-upsert-internal-mode-note" role="status">{uiText("Внутренний маршрут включён. Аудит будет доступен аудиторам и администраторам без ожидания писем и внешних согласований. Существующие аудиты это не затрагивает.")}</div>
                    )}
                  </div>

                  {!modern && <div className="form-group">
                    <label className="form-label">{uiText("Внешний заказчик (исторические данные)")}</label>
                    <input
                      className="ap-control"
                      value={form.external_customer_name}
                      onChange={(e) =>
                        handleChange("external_customer_name", e.target.value)
                      }
                    />
                  </div>}
                </div>

                <div className="form-row audit-upsert-grid audit-upsert-classification-grid">
                  <div className="form-group">
                    <label className="form-label">{uiText("Адрес площадки")}</label>
                    <input
                      className="ap-control"
                      value={form.site_address}
                      onChange={(e) => handleChange("site_address", e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label required">{uiText("Тип аудита")}</label>
                    <select
                      className="ap-control"
                      value={form.audit_type}
                      onChange={(e) => handleAuditKindChange(e.target.value)}
                      disabled={modern}
                      required
                    >
                      {modern || (taxonomy.audit_kinds || []).length === 0 ? (
                        <option value="compliance">{uiText("Аудит соответствия")}</option>
                      ) : (
                        taxonomy.audit_kinds.map((k) => (
                          <option key={k.code} value={k.code}>{uiText(k.label)}</option>
                        ))
                      )}
                    </select>
                    {selectedAuditKind ? (
                      <div style={{ fontSize: 11, color: 'var(--premium-text-secondary)', marginTop: 4 }}>{uiText(selectedAuditKind.description)}</div>
                    ) : null}
                  </div>

                  {!modern && <div className="form-group">
                    <label className="form-label required">{uiText("Тип объекта проверки")}</label>
                    <select
                      className="ap-control"
                      value={form.subject_kind}
                      onChange={(e) => handleChange("subject_kind", e.target.value)}
                      required
                    >
                      {(taxonomy.subject_kinds || []).length === 0 ? (
                        <option value="supplier">{uiText("Поставщик")}</option>
                      ) : (
                        taxonomy.subject_kinds.map((s) => (
                          <option key={s.code} value={s.code}>{uiText(s.label)}</option>
                        ))
                      )}
                    </select>
                    <div style={{ fontSize: 11, color: 'var(--premium-text-secondary)', marginTop: 4 }}>{uiText("Регламент: ")}<b>{selectedRegulation?.label || form.regulation_profile}</b>
                    </div>
                  </div>}
                  {modern && <div className="form-group">
                    <label className="form-label" htmlFor="audit-conduct-mode">{uiText("Режим проведения")}</label>
                    <select id="audit-conduct-mode" className="ap-control" value={form.audit_mode || 'audit'} disabled={isEdit}
                      onChange={event => setForm(prev => ({ ...prev, audit_mode: event.target.value,
                        ...(event.target.value === 'self_audit' ? { without_representative: false } : {}) }))}>
                      <option value="audit">{uiText("Аудит — заполняет аудитор")}</option>
                      <option value="self_audit">{uiText("Самоаудит — заполняет представитель")}</option>
                    </select>
                    <small className="text-muted audit-mode-explanation">{form.audit_mode === 'self_audit' ? uiText("Представитель отвечает на вопросы и прикладывает доказательства в вопросах с вложениями. Аудитор проверяет и при необходимости корректирует ответы. После заполнения представитель нажимает «Сохранить и отправить».") : uiText("Чек-лист заполняет аудитор. Для ответов и вложений представителя создайте самоаудит и назначьте представителя компании.")}{uiText(" Режим фиксируется при создании; существующие аудиты не меняются.")}</small>
                  </div>}
                </div>

                <div className="ap-step-anchor" id="audit-form-step-schedule">
                  <div className="ap-step-anchor-title">{uiText("4. Сроки и документы")}</div>
                  <div className="ap-step-anchor-description">{uiText("Укажите плановую дату, режим контроля и юридические основания.")}</div>
                </div>

                <div className="form-row audit-upsert-grid audit-upsert-schedule-grid">
                  <div className="form-group">
                    <label className="form-label">{uiText("Плановая дата и время")}</label>
                    <input
                      type="datetime-local"
                      className="ap-control"
                      value={form.scheduled_date}
                      min={!isEdit ? currentDateTimeInputValue() : undefined}
                      max="2100-12-31T23:59"
                      onChange={(e) =>
                        handleChange("scheduled_date", e.target.value)
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">{uiText("Режим контроля")}</label>
                    <select
                      className="ap-control"
                      value={form.control_mode}
                      onChange={(e) => handleChange("control_mode", e.target.value)}
                    >
                      {CONTROL_MODES.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {isEdit ? (
                    <div className="form-group">
                      <label className="form-label">{uiText("План аудита")}</label>
                      <select
                        className="ap-control"
                        value={form.program_id}
                        onChange={(e) => handleChange("program_id", e.target.value)}
                      >
                        <option value="">{uiText("Не выбрана")}</option>
                        {programs.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.number || uiText("План #{v0}", {v0: (item.id)})}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}
                </div>

                {/* Договор-основание и подписант приказа.
                    Подпись на ОРД-документе должен ставить уполномоченный
                    подписант (с доверенностью), и аудит должен быть привязан
                    к коммерческому договору. */}
                <div className="form-row audit-upsert-grid audit-upsert-documents-grid">
                  <div className="form-group">
                    <label className="form-label">{uiText("Договор-основание")}</label>
                    <select
                      className="ap-control"
                      value={form.contract_id}
                      onChange={(e) => handleChange("contract_id", e.target.value)}
                    >
                      <option value="">{uiText("Не выбран")}</option>
                      {filteredContracts.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.number} · {c.kind === 'services' ? uiText("услуги") : c.kind === 'supply' ? uiText("поставка") : c.kind === 'concession' ? uiText("концессия") : uiText("иное")}
                          {c.date_signed ? ` · ${c.date_signed}` : ''}
                        </option>
                      ))}
                    </select>
                    <div style={{ fontSize: 11, color: 'var(--premium-text-secondary)', marginTop: 4 }}>
                      {filteredContracts.length === 0 && (form.audited_company_id || form.customer_company_id)
                        ? <>{uiText("Нет действующих договоров с этой компанией. ")}<Link to="/contracts" target="_blank" rel="noreferrer">{uiText("Создайте договор в справочнике")}</Link>.</>
                        : hasRelevantContracts
                          ? uiText("Юридическое основание для проведения аудита.")
                          : uiText("Показаны все действующие договоры: прямого договора с выбранной компанией нет.")}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">{uiText("Представитель с правом подписи приказа")}</label>
                    <select
                      className="ap-control"
                      value={form.signatory_id}
                      onChange={(e) => handleChange("signatory_id", e.target.value)}
                    >
                      <option value="">{uiText("Не выбран")}</option>
                      {filteredSignatories.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.full_name}{s.position ? ` — ${s.position}` : ''}
                        </option>
                      ))}
                    </select>
                    <div style={{ fontSize: 11, color: 'var(--premium-text-secondary)', marginTop: 8 }}>{uiText("Уполномоченное лицо со стороны Заказчика. Нет нужного человека? ")}<Link to="/companies" target="_blank" rel="noreferrer">{uiText("Откройте компанию → Сотрудники → Права подписи")}</Link>.
                    </div>
                  </div>
                </div>

                {/* Краткая история по проверяемой компании — даёт аудитору контекст
                    при планировании. Показывается только если выбрана компания и
                    есть хотя бы один прошлый аудит. */}
                {companySummary && companySummary.total_audits > 0 ? (
                  <div className="alert" style={{ background: '#f0f9ff', border: '1px solid #bae6fd', color: '#0369a1', marginBottom: 14 }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>{uiText("История компании: ")}{companySummary.total_audits}{uiText(" аудит(ов)")}</div>
                    {companySummary.active_audits > 0 ? <div style={{ fontSize: 13, marginBottom: 6 }}><b>{uiText("Сейчас продолжается: ")}{companySummary.active_audits}</b>{uiText(". Проверьте, не дублирует ли новый аудит уже открытую проверку.")}</div> : null}
                    {companySummary.last_audits?.[0] ? <div style={{ fontSize: 13, marginBottom: 6 }}>{uiText("Последняя запись: ")}<b>{companySummary.last_audits[0].title}</b> · {formatStatus(companySummary.last_audits[0].status, 'audit')}.
                    </div> : null}
                    {companySummary.last_compliance_index != null ? <div style={{ fontSize: 13, marginBottom: 6 }}>{uiText("ИС последнего завершённого аудита: ")}<b>{companySummary.last_compliance_index}</b>.
                    </div> : null}
                    {companySummary.recommendation ? (
                      <div style={{ fontSize: 12 }}>{companySummary.recommendation}</div>
                    ) : null}
                  </div>
                ) : null}

                <div className="ap-step-anchor" id="audit-form-step-review">
                  <div className="ap-step-anchor-title">{uiText("5. Проверка и сохранение")}</div>
                  <div className="ap-step-anchor-description">{uiText("Проверьте основание, подписанта и статус перед сохранением.")}</div>
                </div>

                <div className="form-row audit-upsert-grid audit-upsert-basis-grid">
                  <div className="form-group">
                    <label className="form-label">{uiText("Основание")}</label>
                    <input
                      className="ap-control"
                      value={form.audit_basis}
                      onChange={(e) => handleChange("audit_basis", e.target.value)}
                      placeholder={uiText("План закупок / заявка / внешний заказ")}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">{uiText("Подпись / ФИО подписанта (если без реестра)")}</label>
                    <input
                      className="ap-control"
                      value={form.signature_name}
                      onChange={(e) =>
                        handleChange("signature_name", e.target.value)
                      }
                      placeholder={uiText("ФИО, должность")}
                    />
                  </div>
                </div>

                {isEdit ? (
                  <div className="form-row audit-upsert-grid audit-upsert-metadata-grid">
                    <div className="form-group">
                      <label className="form-label">{uiText("Статус")}</label>
                      <select
                        className="ap-control"
                        value={form.status}
                        onChange={(e) => handleChange("status", e.target.value)}
                      >
                        {STATUS_OPTIONS.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">{uiText("ID аудита")}</label>
                      <input
                        className="ap-control"
                        value={audit?.id || "—"}
                        disabled
                      />
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>

          <div className="ap-dialog__footer">
            <button
              type="button"
              className="ap-button ap-button--secondary"
              onClick={onClose}
              disabled={saving}
            >{uiText("Отмена")}</button>

            <button
              type="submit"
              className="ap-button ap-button--primary"
              disabled={loading || saving || !!buffer.recovery}
            >
              {saving
                ? uiText("Сохранение...")
                : isEdit
                  ? uiText("Сохранить изменения")
                  : uiText("Создать аудит")}
            </button>
          </div>
        </form>
    </Dialog>
  );

  return (
    <>
      {modalNode}
      {templatePickerOpen && !templateLocked ? (
        <TemplatePickerModal
          templates={templates}
          selectedId={form.template_id}
            auditKind={modern ? '' : form.audit_type}
          auditKindLabel={uiText(selectedAuditKind?.label || form.audit_type)}
          onSelect={(templateId) => handleChange("template_id", templateId)}
          onClose={closeTemplatePicker}
        />
      ) : null}
    </>
  );
}
